import React from 'react'
import { renderToString } from 'react-dom/server'
import { resolveTextStyle, resolveTableCellStyle, tableCellCss, convertHtmlAlphaColors, getLayerZIndex } from '../../lib/global/helpers'
import { getTemplateBands, BAND_TYPES, BAND_LINE_HEIGHT } from '../../lib/bands/bandModel'

// Saved HTML for one band of a bands-format template (docs/bands-spec.md
// §10). Uses the same markup as the four-section HTML templates, which the
// PDF backend reads text boxes and table headings from; shapes, QR codes,
// images and the logo are drawn from the template JSON, so only markers for
// them are written here. Positions are px relative to the band.
export default function HTMLBandTemplate({ template, band }) {
    const content = template[band.section] || {};
    const isHead = band.section === 'head';

    const textStyle = (style, options) => {
        const resolved = resolveTextStyle({ ...(style || {}) }, content.font, template.prop, options);
        resolved.left = resolved.left ? parseInt(resolved.left) : 0;
        resolved.top = resolved.top ? parseInt(resolved.top) : 0;
        return resolved;
    };
    const layoutFlags = (data) => ({
        ...(data.collapseEmpty ? { 'data-collapse-empty': 'true' } : {}),
        ...(data.keepInRow ? { 'data-keep-in-row': 'true' } : {}),
        ...(data.followLeft ? { 'data-follow-left': 'true' } : {}),
        ...(data.followRight ? { 'data-follow-right': 'true' } : {}),
    });

    const renderElements = () =>
        Object.keys(content.elements || {}).map((key) => {
            const data = content.elements[key];
            if (!data) return null;
            return (
                <div
                    key={data.id || key}
                    style={textStyle(data.style)}
                    id={data.id}
                    data-element={data.name || 'element'}
                    data-section={band.section}
                    data-path="elements"
                    data-index={data.name ? data.name.replace(/\./g, '') : (data.id || `element-${key}`)}
                    {...layoutFlags(data)}
                >
                    {data.text || ''}
                </div>
            );
        });

    const renderCompanyElements = () =>
        Object.keys((isHead && content.companyProps) || {}).map((key) => {
            const prop = content.companyProps[key];
            if (!prop || !prop.show) return null;
            return (
                <div
                    key={key}
                    style={textStyle(prop.style)}
                    id={prop.id}
                    data-element={prop.name || 'company-prop'}
                    data-section="head"
                    data-path="companyProps"
                    data-index={prop.data || key}
                    {...layoutFlags(prop)}
                >
                    {prop.text || ''}
                </div>
            );
        });

    const renderDocumentTitle = () => {
        const title = isHead && content.documentTitle;
        if (!title || !title.show) return null;
        return (
            <div
                style={textStyle(title.style, { defaultFontSize: '20pt', ignoreSectionColor: true })}
                id={title.id}
                data-element={title.name || 'document-title'}
                data-section="head"
                data-path="documentTitle"
                data-index={title.title || 'title'}
            >
                {title.title || 'INVOICE'}
            </div>
        );
    };

    // Markers only: the backend draws the logo and images from the JSON.
    const renderLogo = () => {
        const logo = isHead && content.logo;
        if (!logo || !logo.showLogo || !logo.logo) return null;
        return (
            <div style={logo.style} data-element={logo.name || 'logo'} data-section="head" data-path="logo" data-index="">
                <img src={logo.logo} alt="Company Logo" style={{ width: '100%', height: '100%', display: 'block' }} />
            </div>
        );
    };

    const renderImages = () =>
        Object.keys(content.images || {}).map((key) => {
            const data = content.images[key];
            if (!data || !data.src) return null;
            return (
                <div key={data.id || key} style={data.style} id={data.id} data-element={data.name} data-section={band.section} data-path="images" data-index={key}>
                    <img src={data.src} alt={data.alt || data.name || ''} style={{ width: '100%', height: '100%', display: 'block' }} />
                </div>
            );
        });

    const renderVatSummary = () => {
        const data = content.VatSummary;
        if (!data || Object.keys(data).length === 0) return null;
        return (
            <div
                style={{ ...textStyle(data.style), zIndex: getLayerZIndex(data, 2) }}
                id={data.id}
                data-element={data.name || 'VatSummary'}
                data-section="VatSummary"
                data-path="elements"
                data-index={data.name ? data.name.replace(/\./g, '') : (data.id || 'vat-summary')}
            >
                {data.text || ''}
            </div>
        );
    };

    if (band.type === BAND_TYPES.ITEMS) {
        const layout = content.layout || {};
        const tableHeader = layout.tableHeader || {};
        const columns = Object.keys(content.items || {}).map((key) => content.items[key]).filter((c) => c && c.width !== undefined);
        const totalWidth = columns.reduce((sum, c) => sum + Number(c.width || 10), 0);

        return (
            <div data-band={band.id} data-band-type={band.type}>
                <table style={{ width: '100%' }} cellSpacing={0} className="items-table">
                    <thead style={{ display: 'table-header-group' }}>
                        <tr>
                            {columns.map((column, i) => (
                                <th
                                    key={`dd_${i}`}
                                    width={(Number(column.width || 10) / totalWidth) * 100 + '%'}
                                    style={{ padding: '10px 0px 10px 10px', ...tableCellCss(resolveTableCellStyle(layout, column, 'header')) }}
                                >
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                        {tableHeader.showBottomBorder && (
                            <tr>
                                <th colSpan={columns.length} style={{ padding: 0 }}>
                                    <div style={{
                                        width: `${tableHeader.bottomBorderWidth ?? 100}%`,
                                        height: `${tableHeader.bottomBorderSize ?? 1}px`,
                                        backgroundColor: tableHeader.bottomBorderColor || '#333333ff'
                                    }} />
                                </th>
                            </tr>
                        )}
                    </thead>
                    {'{TableBodyContent}'}
                </table>
            </div>
        );
    }

    return (
        <div data-band={band.id} data-band-type={band.type} style={{ position: 'relative', height: band.height, lineHeight: BAND_LINE_HEIGHT }}>
            {renderElements()}
            {renderLogo()}
            {renderDocumentTitle()}
            {renderCompanyElements()}
            {renderVatSummary()}
            {renderImages()}
        </div>
    );
}

// HTMLData.Bands for a template: { [band id]: html }, not yet URI-encoded.
export function renderBandsHtml(template) {
    const html = {};
    getTemplateBands(template).forEach((band) => {
        html[band.id] = convertHtmlAlphaColors(renderToString(<HTMLBandTemplate template={template} band={band} />));
    });
    return html;
}
